import { createFrontendUuidv4 } from '@main/utils';
import React, { KeyboardEvent, useMemo } from 'react';

import {
  CenterDot,
  HiddenRadio,
  LabelWrapper,
  RadioInputCircle,
  RadioInputContainer,
} from './wrappers';

/**
 * represents the checked state of the radio input
 */
export interface RadioInputProps
  extends Omit<
    React.ComponentPropsWithRef<'input'>,
    'children' | 'onChange' | 'type' | 'enterKeyHint' | 'name'
  > {
  /** Text for enter key on virtual keyboards */
  enterKeyHint?:
    | 'enter'
    | 'done'
    | 'go'
    | 'next'
    | 'previous'
    | 'search'
    | 'send'
    | undefined;
  /** label element */
  children: React.ReactNode;
  /** callback invoked when input state changes */
  onChange?: () => void;
  /** message for input name */
  name?: string;
  /** value for radio button */
  value?: string;
  /** Show radio circle */
  showCircle?: boolean;
}

export const RadioInput = React.forwardRef<HTMLInputElement, RadioInputProps>(
  (
    {
      children,
      disabled = false,
      checked = false,
      onChange,
      name,
      value,
      showCircle = false,
      ...props
    },
    ref,
  ) => {
    const id = useMemo(() => createFrontendUuidv4(), []);

    return (
      <RadioInputContainer
        onKeyDown={(event: KeyboardEvent) => {
          if (event.key === ' ' && !disabled) onChange?.();
        }}
        onMouseDown={() => {
          if (!disabled) onChange?.();
        }}
        radioInputValue={checked ? 'checked' : 'unchecked'}
        disabled={disabled}
        tabIndex={0}
      >
        <HiddenRadio
          ref={ref}
          id={id}
          type="radio"
          tabIndex={-1}
          // no-op onChange to prevent React warning
          // eslint-disable-next-line no-empty-function
          onChange={() => {}}
          checked={checked}
          disabled={disabled}
          name={name}
          value={value || ''}
          {...props}
        />
        <LabelWrapper htmlFor={id}>
          {showCircle && (
            <RadioInputCircle>{checked && <CenterDot />}</RadioInputCircle>
          )}
          {children}
        </LabelWrapper>
      </RadioInputContainer>
    );
  },
);
